// Preview toolbar — controlled toggle bar for the live preview's
// viewport. Per spec/build-plan.md P-030 row + spec/tech-arch.md §3.3
// + §8.
//
// **Single radiogroup** ("Viewport": Desktop / Mobile) within a wider
// `role="toolbar"` (WAI-ARIA APG composite-widget pattern). The
// Color-scheme cluster (Light / Dark) was removed at P-037 close per
// D-100 alongside the dark-mode shell removal — light-only output.
//
// **Pill-button group.** Active button has filled bg + white text;
// inactive has transparent bg + hover gray. Text labels at V1 (Desktop
// / Mobile) — icons are a future polish revisit. ARIA `role="radio"` +
// `aria-checked` on each `<button>` carries the selection state to
// screen readers.
//
// **Controlled component.** Props `(viewport, onViewportChange)`. State
// lives at the parent screen (P-031/P-032 email editor + P-037 block
// preview) — component state, NOT AppState, to avoid a `schemaVersion`
// bump for a UI signal (D-083 reasoning). Tests host state locally.
//
// **Keyboard accessibility (P-036).** Roving tabindex via the shared
// `useRovingTabIndex` hook. The toolbar carries a single tabstop —
// the currently-active item — matching the WAI-ARIA APG toolbar/
// radiogroup composite-widget pattern. Arrow Right / Arrow Down cycle
// forward; Arrow Left / Arrow Up cycle backward; Home / End jump to
// first / last. Click and programmatic focus update the tabstop via
// each button's `onFocus`.
//
// **`<ToggleButton>` is a file-local sub-component** (2 call sites
// post-D-100). Hoisting waits for a 3rd consumer outside this file.

import type { ComponentChildren } from 'preact'

import { mergeClass } from '../../lib/merge-class'
import type { Viewport } from '../../types'
import { useRovingTabIndex } from '../hooks/use-roving-tabindex'

const TOOLBAR_BASE_CLASS = 'flex items-center gap-4 border-b border-gray-200 bg-white px-4 py-2'

const RADIOGROUP_CLASS = 'flex gap-1'

const BUTTON_BASE_CLASS = 'rounded px-3 py-1 text-sm font-medium transition-colors'
const BUTTON_ACTIVE_CLASS = 'bg-gray-900 text-white'
const BUTTON_INACTIVE_CLASS = 'bg-transparent text-gray-700 hover:bg-gray-100'

interface PreviewToolbarProps {
  readonly viewport: Viewport
  readonly onViewportChange: (viewport: Viewport) => void
  readonly className?: string
}

interface ToggleButtonProps {
  readonly active: boolean
  readonly tabIndex: 0 | -1
  readonly onClick: () => void
  readonly onFocus: () => void
  readonly children: ComponentChildren
}

function ToggleButton({ active, tabIndex, onClick, onFocus, children }: ToggleButtonProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      tabIndex={tabIndex}
      class={`${BUTTON_BASE_CLASS} ${active ? BUTTON_ACTIVE_CLASS : BUTTON_INACTIVE_CLASS}`}
      onClick={onClick}
      onFocus={onFocus}
    >
      {children}
    </button>
  )
}

export function PreviewToolbar({ viewport, onViewportChange, className }: PreviewToolbarProps) {
  const defaultIndex = viewport === 'mobile' ? 1 : 0
  const { rootRef, activeIndex, setActiveIndex } = useRovingTabIndex<HTMLDivElement>({
    defaultIndex,
  })

  return (
    <div
      ref={rootRef}
      role="toolbar"
      aria-label="Preview controls"
      class={mergeClass(TOOLBAR_BASE_CLASS, className)}
    >
      <div role="radiogroup" aria-label="Viewport" class={RADIOGROUP_CLASS}>
        <ToggleButton
          active={viewport === 'desktop'}
          tabIndex={activeIndex === 0 ? 0 : -1}
          onClick={() => onViewportChange('desktop')}
          onFocus={() => setActiveIndex(0)}
        >
          Desktop
        </ToggleButton>
        <ToggleButton
          active={viewport === 'mobile'}
          tabIndex={activeIndex === 1 ? 0 : -1}
          onClick={() => onViewportChange('mobile')}
          onFocus={() => setActiveIndex(1)}
        >
          Mobile
        </ToggleButton>
      </div>
    </div>
  )
}
